"use client";

import { useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";

const rules = [
  {
    id: "free",
    icon: "LockOpenIcon" as const,
    label: "Free Tier",
    headline: "Ads Are Fair.",
    body: "You chose the free option. The platform covers its costs through advertising. This is a clear, honest exchange. No problem here.",
    verdict: "✓ Acceptable",
    verdictColor: "text-green-600",
    bgAccent: "#F0FFF4",
    borderColor: "border-green-200",
  },
  {
    id: "paid",
    icon: "CreditCardIcon" as const,
    label: "Paid Tier",
    headline: "No Ads Allowed.",
    body: "You are paying a monthly subscription specifically to remove ads. The platform has already been compensated. Running ads here is double-billing your attention.",
    verdict: "⚠ Violation",
    verdictColor: "text-amber-600",
    bgAccent: "#FFFBEB",
    borderColor: "border-amber-200",
  },
  {
    id: "rule",
    icon: "XCircleIcon" as const,
    label: "The Rule",
    headline: "Switch. Every Time.",
    body: "If a brand interrupts your paid stream, immediately switch to their direct competitor and publicly say why. Make the cost of the double-dip higher than the revenue.",
    verdict: "🚨 Action Required",
    verdictColor: "text-[#FF2222]",
    bgAccent: "#FFF5F5",
    borderColor: "border-red-200",
  },
];

export default function LogicSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll(".logic-card");
            cards.forEach((card, i) => {
              setTimeout(() => {
                card.classList.remove("opacity-0-init");
                card.classList.add("animate-fade-up");
              }, i * 150);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="how-it-works"
      ref={sectionRef}
      className="bg-surface py-24 md:py-32 px-6 md:px-12"
      aria-label="How the movement works"
    >
      <div className="max-w-8xl mx-auto">
        {/* Section header */}
        <div className="mb-16 max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-widest text-muted block mb-4">
            The Flowchart of Fairness
          </span>
          <h2 className="font-display text-5xl md:text-6xl font-black text-ink leading-[0.95] tracking-tightest mb-6">
            The Logic Is<br />
            <span className="italic font-normal text-[#FF2222]">Simple.</span>
          </h2>
          <p className="text-lg text-mid font-light leading-relaxed">
            Three rules. No exceptions. If you break them, we walk — and we take our wallets with us.
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {rules.map((rule, index) => (
            <div
              key={rule.id}
              className={`logic-card opacity-0-init relative flex flex-col p-8 rounded-4xl border-2 ${rule.borderColor} bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-xl`}
              style={{ background: rule.bgAccent }}
            >
              {/* Step number */}
              <span className="absolute top-6 right-6 font-display text-7xl font-black text-ink opacity-[0.06] leading-none select-none">
                {index + 1}
              </span>

              {/* Icon */}
              <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center shadow-sm mb-6">
                <Icon name={rule.icon} size={22} variant="outline" className="text-ink" />
              </div>

              {/* Label */}
              <span className="text-xs font-bold uppercase tracking-widest text-muted mb-2">
                {rule.label}
              </span>

              {/* Headline */}
              <h3 className="font-display text-3xl font-black text-ink leading-tight tracking-tight mb-4">
                {rule.headline}
              </h3>

              {/* Body */}
              <p className="text-sm text-mid leading-relaxed font-light flex-1 mb-6">
                {rule.body}
              </p>

              {/* Verdict */}
              <div className={`text-sm font-bold ${rule.verdictColor}`}>
                {rule.verdict}
              </div>
            </div>
          ))}
        </div>

        {/* Flow connector visual */}
        <div className="mt-12 flex items-center justify-center gap-4 text-sm font-medium text-muted">
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-green-400 inline-block" />
            Free tier? Fine.
          </span>
          <Icon name="ArrowRightIcon" size={16} variant="outline" className="text-muted" />
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-amber-400 inline-block" />
            Paid tier + ads?
          </span>
          <Icon name="ArrowRightIcon" size={16} variant="outline" className="text-muted" />
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-[#FF2222] inline-block" />
            We act. Now.
          </span>
        </div>
      </div>
    </section>
  );
}